import apiClient, { API_ENDPOINTS, appendPath, rejectWithError } from "api";
import {
  buildAcademyExamByStoredUserCriteria,
  buildAcademyExamCriteria,
  buildPrimaryIdCriteria
} from "domains/education/services/examRequestBuilders";

const { curriculum, exam } = API_ENDPOINTS;
const acceptJsonHeaders = { Accept: "application/json" };

const rejectExamError = (error, label) => rejectWithError(error, label);

export const normalizeAcademyExamResponse = (response) => (
  Array.isArray(response) ? response : response ? [response] : []
);

export const startExam = async (id) => {
  try {
    return await apiClient.post(exam.startExam, undefined, {
      headers: acceptJsonHeaders,
      query: { id }
    });
  } catch (error) {
    return rejectExamError(error, "Error starting exam");
  }
};

export const getExam = (id) => async (dispatch) => {
  try {
    const examData = await apiClient.get(appendPath(exam.getExam, id), {
      headers: acceptJsonHeaders
    });
    dispatch({ type: "GET_EXAM_SUCCESS", payload: examData });
    return examData;
  } catch (error) {
    dispatch({ type: "GET_EXAM_FAILURE", payload: error?.message });
    throw error;
  }
};

export const getExamByPrimaryId = async (id) => {
  try {
    return await apiClient.get(appendPath(exam.getExam, id), {
      headers: acceptJsonHeaders
    });
  } catch (error) {
    return rejectExamError(error, "Error getting exam by primary id");
  }
};

export const getAcademyExams = async (params) => {
  try {
    return await apiClient.post(exam.getAcademyExams, undefined, {
      headers: acceptJsonHeaders,
      query: params
    });
  } catch (error) {
    return rejectExamError(error, "Error getting academy exams");
  }
};

export const deleteAcademyExam = async (id) => {
  try {
    return await apiClient.post(exam.deleteAcademyExam, undefined, {
      headers: acceptJsonHeaders,
      query: { id }
    });
  } catch (error) {
    return rejectExamError(error, "Error deleting academy exam");
  }
};

export const saveExamProgress = async (examPayload) => {
  try {
    return await apiClient.post(exam.saveExamProgress, examPayload, {
      headers: acceptJsonHeaders
    });
  } catch (error) {
    return rejectExamError(error, "Error saving exam progress");
  }
};

export const updateUserCurriculum = async (userCurriculumData) => {
  try {
    return await apiClient.post(curriculum.updateUserCurriculum, userCurriculumData, {
      headers: acceptJsonHeaders
    });
  } catch (error) {
    return rejectExamError(error, "Error updating user curriculum");
  }
};

export const updateUserCurriculumProgressStatus = async (userId, scheduleProductId) => {
  try {
    return await apiClient.put(curriculum.updateUserCurriculumProgressStatus, undefined, {
      query: {
        userId,
        scheduleProductId
      }
    });
  } catch (error) {
    return rejectExamError(error, "Error updating user curriculum progress status");
  }
};

export const getAcademyExam = async (
  companyPrimaryId,
  userPrimaryId,
  examPrimaryId,
  scheduledProductId,
  deliveryType,
  deliverySubType,
  productPrimaryId
) => {
  try {
    return await apiClient.post(
      exam.getAcademyExam,
      buildAcademyExamCriteria({
        companyPrimaryId,
        userPrimaryId,
        examPrimaryId,
        scheduledProductId,
        deliveryType,
        deliverySubType,
        productPrimaryId
      }),
      { headers: acceptJsonHeaders }
    );
  } catch (error) {
    return rejectExamError(error, "Error getting academy exam");
  }
};

export const getAcademyExamByPrimaryId = async (primaryId, companyId) => {
  try {
    void companyId;

    const response = await apiClient.post(
      exam.getAcademyExam,
      buildPrimaryIdCriteria(primaryId),
      { headers: acceptJsonHeaders }
    );
    return normalizeAcademyExamResponse(response);
  } catch (error) {
    return rejectExamError(error, "Error getting academy exam by primary id");
  }
};

export const getExamToStart = async (examPrimaryId) => {
  try {
    return await apiClient.post(
      exam.getAcademyExam,
      buildPrimaryIdCriteria(examPrimaryId),
      { headers: acceptJsonHeaders }
    );
  } catch (error) {
    return rejectExamError(error, "Error getting exam to start");
  }
};

export const getAcademyExamByUserId = async (userId) => {
  try {
    const companyPrimaryId = localStorage.getItem("CompPrimaryId");
    const userPrimaryId = userId || localStorage.getItem("userId");

    return await apiClient.post(
      exam.getAcademyExam,
      buildAcademyExamByStoredUserCriteria({
        companyPrimaryId,
        userPrimaryId
      }),
      { headers: acceptJsonHeaders }
    );
  } catch (error) {
    return rejectExamError(error, "Error getting academy exam by user id");
  }
};

export const createNewExam = async (examPayload) => {
  try {
    return await apiClient.post(exam.createNewExam, examPayload, {
      headers: acceptJsonHeaders
    });
  } catch (error) {
    return rejectExamError(error, "Error creating new exam");
  }
};

export const evaluateExam = async (examData) => {
  try {
    return await apiClient.post(exam.evaluateExam, examData, {
      headers: acceptJsonHeaders
    });
  } catch (error) {
    return rejectExamError(error, "Error evaluating exam");
  }
};
